import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, LogOut, Brain, Compass, BookOpen, Heart } from 'lucide-react';
import { useAuth } from '../App';

const ComingSoonDashboard = ({ portalType = 'client' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isCoach = portalType === 'coach';

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="coming-soon-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E7E5E4]">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl py-4 flex items-center justify-between">
          <Link to="/" className="font-['Playfair_Display'] text-2xl text-[#2C5530]">
            Quiet Mind Golf
          </Link>
          <div className="flex items-center gap-4">
            {user && <span className="text-sm text-[#737373]">{user.full_name || user.email}</span>}
            <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-[#57534E] hover:text-[#1A1A1A] transition-colors">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 max-w-4xl py-20">
        {/* Coming Soon Hero */}
        <div className="text-center mb-16">
          <div className="w-16 h-16 rounded-full bg-[#2C5530]/10 flex items-center justify-center mx-auto mb-6">
            <Brain className="w-8 h-8 text-[#2C5530]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1A1A1A] mb-4">
            {isCoach ? 'Coach Portal' : 'Your Dashboard'}
          </h1>
          <div className="inline-block bg-[#BC562B]/10 text-[#BC562B] font-semibold text-sm px-5 py-2 rounded-full mb-6">
            Coming Soon
          </div>
          <p className="text-[#57534E] text-lg max-w-2xl mx-auto leading-relaxed">
            {isCoach 
              ? "We're building a powerful coaching platform to help you support your players' mental game. Stay tuned."
              : "We're redesigning your experience to focus on what matters most — building mental skills, willingness, acceptance, and confidence. Something better is on its way."
            }
          </p>
        </div>

        {/* Player's Locker — primary CTA (clients only) */}
        {!isCoach && (
          <div className="mb-12 max-w-3xl mx-auto" data-testid="locker-cta-section">
            <Link
              to="/locker"
              className="block group bg-gradient-to-br from-[#1A2E1D] to-[#2C5530] text-white rounded-lg p-7 md:p-8 hover:shadow-2xl transition-all"
              data-testid="open-locker-btn"
            >
              <div className="flex items-start md:items-center gap-5 flex-col md:flex-row">
                <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <span className="inline-block bg-[#BC562B] text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 mb-3">Awareness · Acceptance · Action</span>
                  <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-2 text-white group-hover:text-[#BC562B] transition-colors">
                    Open Your Player's Locker
                  </h3>
                  <p className="text-white/80 text-sm md:text-base leading-relaxed">
                    Log rounds across all 3 KPIs, surface your Awareness−Action gap, and see your trends.
                  </p>
                </div>
                <ArrowRight className="w-6 h-6 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
              </div>
            </Link>
          </div>
        )}

        {/* Coach Locker view — for coaches */}
        {isCoach && (
          <div className="mb-12 max-w-3xl mx-auto" data-testid="coach-locker-cta-section">
            <Link
              to="/coach/locker"
              className="block group bg-gradient-to-br from-[#1A2E1D] to-[#2C5530] text-white rounded-lg p-7 md:p-8 hover:shadow-2xl transition-all"
              data-testid="open-coach-locker-btn"
            >
              <div className="flex items-start md:items-center gap-5 flex-col md:flex-row">
                <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <span className="inline-block bg-[#BC562B] text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 mb-3">Player's Locker · Coach view</span>
                  <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-2 text-white group-hover:text-[#BC562B] transition-colors">
                    See your players' Locker
                  </h3>
                  <p className="text-white/80 text-sm md:text-base leading-relaxed">
                    Read every linked student's Awareness, Acceptance & Action — and leave notes on their rounds.
                  </p>
                </div>
                <ArrowRight className="w-6 h-6 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
              </div>
            </Link>
          </div>
        )}

        {/* Available Content */}
        {!isCoach && (
          <div>
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] text-center mb-8">
              Explore While You Wait
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* QMG Blueprint */}
              <div className="bg-white border border-[#E7E5E4] rounded-lg p-6 hover:border-[#2C5530] transition-all group">
                <div className="w-10 h-10 rounded-lg bg-[#2C5530]/10 flex items-center justify-center mb-4">
                  <Brain className="w-5 h-5 text-[#2C5530]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">The QMG Blueprint</h3>
                <p className="text-sm text-[#737373] mb-4">A proven framework for mental movement under pressure — Awareness, Acceptance, Action.</p>
                <div className="space-y-2">
                  <Link to="/mac/mindful" className="flex items-center gap-3 p-3 bg-[#F9F7F4] rounded-lg hover:bg-[#2C5530]/10 transition-colors group/link" data-testid="link-mindful">
                    <span className="w-8 h-8 rounded-full border border-[#1A2E1D]/20 flex items-center justify-center flex-shrink-0 text-[#1A2E1D] group-hover/link:bg-[#1A2E1D] group-hover/link:text-white transition-colors">
                      <Brain className="w-4 h-4" />
                    </span>
                    <span className="text-sm font-medium text-[#1A1A1A] flex-1">Awareness — Step Back</span>
                    <ArrowRight className="w-4 h-4 text-[#A8A29E] group-hover/link:text-[#2C5530]" />
                  </Link>
                  <Link to="/mac/accepting" className="flex items-center gap-3 p-3 bg-[#F9F7F4] rounded-lg hover:bg-[#2C5530]/10 transition-colors group/link" data-testid="link-accepting">
                    <span className="w-8 h-8 rounded-full border border-[#1A2E1D]/20 flex items-center justify-center flex-shrink-0 text-[#1A2E1D] group-hover/link:bg-[#1A2E1D] group-hover/link:text-white transition-colors">
                      <Heart className="w-4 h-4" />
                    </span>
                    <span className="text-sm font-medium text-[#1A1A1A] flex-1">Acceptance — Step Up</span>
                    <ArrowRight className="w-4 h-4 text-[#A8A29E] group-hover/link:text-[#2C5530]" />
                  </Link>
                  <Link to="/mac/commitment" className="flex items-center gap-3 p-3 bg-[#F9F7F4] rounded-lg hover:bg-[#2C5530]/10 transition-colors group/link" data-testid="link-commitment">
                    <span className="w-8 h-8 rounded-full border border-[#1A2E1D]/20 flex items-center justify-center flex-shrink-0 text-[#1A2E1D] group-hover/link:bg-[#1A2E1D] group-hover/link:text-white transition-colors">
                      <ArrowRight className="w-4 h-4" />
                    </span>
                    <span className="text-sm font-medium text-[#1A1A1A] flex-1">Action — Step In</span>
                    <ArrowRight className="w-4 h-4 text-[#A8A29E] group-hover/link:text-[#2C5530]" />
                  </Link>
                </div>
              </div>

              {/* The Mind Factor */}
              <div className="bg-white border border-[#E7E5E4] rounded-lg p-6 hover:border-[#BC562B] transition-all group">
                <div className="w-10 h-10 rounded-lg bg-[#BC562B]/10 flex items-center justify-center mb-4">
                  <Compass className="w-5 h-5 text-[#BC562B]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">The Mind Factor</h3>
                <p className="text-sm text-[#737373] mb-4">Four key areas for balanced improvement — train your mind across every phase of the game.</p>
                <div className="space-y-2">
                  <Link to="/the-approach/before" className="flex items-center justify-between p-3 bg-[#F9F7F4] rounded-lg hover:bg-[#BC562B]/10 transition-colors group/link" data-testid="link-before">
                    <span className="text-sm font-medium text-[#1A1A1A]">Before — Training Body & Brain</span>
                    <ArrowRight className="w-4 h-4 text-[#A8A29E] group-hover/link:text-[#BC562B]" />
                  </Link>
                  <Link to="/the-approach/during" className="flex items-center justify-between p-3 bg-[#F9F7F4] rounded-lg hover:bg-[#BC562B]/10 transition-colors group/link" data-testid="link-during">
                    <span className="text-sm font-medium text-[#1A1A1A]">During — The Playing Zone</span>
                    <ArrowRight className="w-4 h-4 text-[#A8A29E] group-hover/link:text-[#BC562B]" />
                  </Link>
                  <Link to="/the-approach/in-between" className="flex items-center justify-between p-3 bg-[#F9F7F4] rounded-lg hover:bg-[#BC562B]/10 transition-colors group/link" data-testid="link-in-between">
                    <span className="text-sm font-medium text-[#1A1A1A]">In-Between — Mental Recharge</span>
                    <ArrowRight className="w-4 h-4 text-[#A8A29E] group-hover/link:text-[#BC562B]" />
                  </Link>
                  <Link to="/the-approach/after" className="flex items-center justify-between p-3 bg-[#F9F7F4] rounded-lg hover:bg-[#BC562B]/10 transition-colors group/link" data-testid="link-after">
                    <span className="text-sm font-medium text-[#1A1A1A]">After — Intelligent Reflection</span>
                    <ArrowRight className="w-4 h-4 text-[#A8A29E] group-hover/link:text-[#BC562B]" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Research Link */}
            <div className="text-center mt-10">
              <Link to="/mac-research" className="inline-flex items-center gap-2 text-[#2C5530] font-semibold hover:underline">
                <BookOpen className="w-4 h-4" />
                Read the Research Behind the QMG Blueprint
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-[#E7E5E4] py-6 text-center">
        <Link to="/" className="text-sm text-[#737373] hover:text-[#2C5530] transition-colors">
          ← Back to Quiet Mind Golf
        </Link>
      </footer>
    </div>
  );
};

export default ComingSoonDashboard;
